// shell/components/entity-fields/form-renderers/file-input.ts

import { createElement, clearElement } from '../../../utils/dom';
import { formatFileSize } from '../../../utils/format';
import type { FileInputFormField } from '../../entity-view/types';
import type { FormRendererResult } from './types';

export interface UploadedFile {
    id: string;
    name: string;
    size: number;
    mimeType: string;
    file?: File;
    previewUrl?: string;
}

/**
 * Renders a file input with drag-drop and preview.
 */
export function renderFileInput<T>(
    field: FileInputFormField<T>,
    initialValue: unknown,
    onChange: (value: UploadedFile[]) => void
): FormRendererResult {
    const container = createElement('div', {
        className: 'ev-field-input ev-field-input--file',
    });

    // Current files
    let files: UploadedFile[] = [];
    if (Array.isArray(initialValue)) {
        files = initialValue as UploadedFile[];
    }

    // Hidden file input
    const inputEl = createElement('input', {
        className: 'ev-file-hidden-input',
        attributes: {
            type: 'file',
            ...(field.accept ? { accept: field.accept } : {}),
            ...(field.multiple ? { multiple: '' } : {}),
            ...(field.disabled ? { disabled: '' } : {}),
        },
    }) as HTMLInputElement;

    // Drop zone
    const dropZone = createElement('div', {
        className: 'ev-file-drop-zone',
        attributes: {
            role: 'button',
            tabindex: '0',
        },
    });

    const dropText = createElement('div', {
        className: 'ev-file-drop-text',
    });
    const icon = createElement('span', {
        className: 'ev-file-drop-icon',
        textContent: '📁',
    });
    const label = createElement('span', {
        className: 'ev-file-drop-label',
        textContent: 'Drop files here or click to browse',
    });
    dropText.appendChild(icon);
    dropText.appendChild(label);
    dropZone.appendChild(dropText);

    // Files list
    const filesList = createElement('div', {
        className: 'ev-files-list',
    });

    // Render files
    const renderFiles = () => {
        clearElement(filesList);

        for (const file of files) {
            const item = createElement('div', {
                className: 'ev-file-item',
            });

            // Preview for images
            if (field.showPreview && file.previewUrl && file.mimeType.startsWith('image/')) {
                const preview = createElement('img', {
                    className: 'ev-file-preview',
                    attributes: { src: file.previewUrl, alt: file.name },
                });
                item.appendChild(preview);
            }

            // File info
            const info = createElement('div', {
                className: 'ev-file-info',
            });
            const name = createElement('span', {
                className: 'ev-file-name',
                textContent: file.name,
            });
            const size = createElement('span', {
                className: 'ev-file-size',
                textContent: formatFileSize(file.size),
            });
            info.appendChild(name);
            info.appendChild(size);
            item.appendChild(info);

            // Remove button
            if (!field.disabled) {
                const removeBtn = createElement('button', {
                    className: 'ev-file-remove',
                    textContent: '×',
                    attributes: { type: 'button', 'aria-label': `Remove ${file.name}` },
                    onClick: () => removeFile(file.id),
                });
                item.appendChild(removeBtn);
            }

            filesList.appendChild(item);
        }
    };

    // Add files
    const addFiles = (newFiles: FileList) => {
        const maxFiles = field.maxFiles ?? Infinity;
        const currentCount = files.length;

        for (let i = 0; i < newFiles.length && currentCount + i < maxFiles; i++) {
            const file = newFiles[i];

            // Check size
            if (field.maxSize && file.size > field.maxSize) {
                console.warn(`File ${file.name} exceeds max size`);
                continue;
            }

            const uploaded: UploadedFile = {
                id: generateId(),
                name: file.name,
                size: file.size,
                mimeType: file.type,
                file: file,
            };

            // Generate preview for images
            if (field.showPreview && file.type.startsWith('image/')) {
                uploaded.previewUrl = URL.createObjectURL(file);
            }

            files = [...files, uploaded];
        }

        renderFiles();
        onChange(files);
    };

    // Remove file
    const removeFile = (id: string) => {
        const file = files.find(f => f.id === id);
        if (file?.previewUrl) {
            URL.revokeObjectURL(file.previewUrl);
        }
        files = files.filter(f => f.id !== id);
        renderFiles();
        onChange(files);
    };

    // Handle file input change
    const handleChange = () => {
        if (inputEl.files && inputEl.files.length > 0) {
            addFiles(inputEl.files);
            inputEl.value = ''; // Reset for re-upload
        }
    };

    // Handle click on drop zone
    const handleClick = () => {
        if (!field.disabled) {
            inputEl.click();
        }
    };

    // Handle keyboard
    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
        }
    };

    // Drag and drop
    const handleDragOver = (e: DragEvent) => {
        e.preventDefault();
        dropZone.classList.add('ev-file-drop-zone--dragover');
    };

    const handleDragLeave = () => {
        dropZone.classList.remove('ev-file-drop-zone--dragover');
    };

    const handleDrop = (e: DragEvent) => {
        e.preventDefault();
        dropZone.classList.remove('ev-file-drop-zone--dragover');

        if (e.dataTransfer?.files && !field.disabled) {
            addFiles(e.dataTransfer.files);
        }
    };

    // Wire up events
    inputEl.addEventListener('change', handleChange);
    dropZone.addEventListener('click', handleClick);
    dropZone.addEventListener('keydown', handleKeyDown);
    dropZone.addEventListener('dragover', handleDragOver);
    dropZone.addEventListener('dragleave', handleDragLeave);
    dropZone.addEventListener('drop', handleDrop);

    // Build container
    container.appendChild(inputEl);
    container.appendChild(dropZone);
    container.appendChild(filesList);

    // Initial render
    renderFiles();

    return {
        element: container,
        getValue: () => files,
        setValue: (value: unknown) => {
            // Revoke old preview URLs
            for (const file of files) {
                if (file.previewUrl) {
                    URL.revokeObjectURL(file.previewUrl);
                }
            }

            if (Array.isArray(value)) {
                files = value as UploadedFile[];
            } else {
                files = [];
            }
            renderFiles();
        },
        validate: async () => true,
        focus: () => dropZone.focus(),
        dispose: () => {
            // Revoke all preview URLs
            for (const file of files) {
                if (file.previewUrl) {
                    URL.revokeObjectURL(file.previewUrl);
                }
            }
            inputEl.removeEventListener('change', handleChange);
            dropZone.removeEventListener('click', handleClick);
            dropZone.removeEventListener('keydown', handleKeyDown);
            dropZone.removeEventListener('dragover', handleDragOver);
            dropZone.removeEventListener('dragleave', handleDragLeave);
            dropZone.removeEventListener('drop', handleDrop);
        },
    };
}



function generateId(): string {
    return `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
